import * as React from "react";
import { TicketEvent, TicketEventLocation } from "./ticketEventTypes";

const parseLocation = (location?: TicketEventLocation): string => {
  if (!location) {
    return "-";
  }

  if (typeof location === "string") {
    try {
      const parsedLocation = JSON.parse(location);

      if (parsedLocation && typeof parsedLocation === "object") {
        return parseLocation(parsedLocation);
      }

      return location;
    } catch (error) {
      return location;
    }
  }

  if (typeof location === "object") {
    const lines = [
      location.address,
      location.city,
      location.country_code || location.country,
    ].filter(Boolean);

    return lines.length ? lines.join(", ") : "-";
  }

  return "-";
};

export const getDisplayStatus = (
  status?: string,
  endDate?: string
): string | undefined => {
  if (!status) {
    return status;
  }

  if (status === "active" && endDate) {
    const parsedEndDate = new Date(endDate);
    const now = new Date();

    if (!Number.isNaN(parsedEndDate.getTime()) && parsedEndDate < now) {
      return "completed";
    }
  }

  return status;
};

export const statusTextColour = (status?: string, endDate?: string): string => {
  const resolvedStatus = getDisplayStatus(status, endDate);

  switch (resolvedStatus) {
    case "active":
      return "#2EAD4B";
    case "pending_approval":
      return "#D6A623";
    case "changes_requested":
      return "#C9792B";
    case "draft":
      return "#7A7A7A";
    case "cancelled":
      return "#CC4B4B";
    case "completed":
      return "#7A7A7A";
    default:
      return "#666";
  }
};

export const renderStatusBadge = (status?: string, endDate?: string) => {
  const resolvedStatus = getDisplayStatus(status, endDate);

  if (!resolvedStatus) {
    return "-";
  }

  const label = resolvedStatus
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  const statusStyles = (() => {
    switch (resolvedStatus) {
      case "active":
        return {
          color: "#2EAD4B",
          backgroundColor: "#EBF8EE",
          border: "1px solid #BFE5C9",
        };
      case "pending_approval":
        return {
          color: "#D6A623",
          backgroundColor: "#FFF7E6",
          border: "1px solid #F1D58A",
        };
      case "changes_requested":
        return {
          color: "#C9792B",
          backgroundColor: "#FFF1E8",
          border: "1px solid #E9BE97",
        };
      case "draft":
        return {
          color: "#7A7A7A",
          backgroundColor: "#F3F4F6",
          border: "1px solid #D6D9DE",
        };
      case "cancelled":
        return {
          color: "#CC4B4B",
          backgroundColor: "#FDEDED",
          border: "1px solid #E7B4B4",
        };
      case "completed":
        return {
          color: "#7A7A7A",
          backgroundColor: "#F3F4F6",
          border: "1px solid #D6D9DE",
        };
      default:
        return {
          color: "#666",
          backgroundColor: "#F8F8F8",
          border: "1px solid #DDD",
        };
    }
  })();

  return (
    <span
      style={{
        ...statusStyles,
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: "999px",
        fontWeight: 700,
        fontSize: "13px",
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

export const renderColour = (colour?: string) => {
  if (!colour) {
    return "-";
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
      }}
    >
      <div
        style={{
          width: "28px",
          height: "28px",
          backgroundColor: colour,
          border: "1px solid #bdbdbd",
          borderRadius: "4px",
          flexShrink: 0,
        }}
      />
      <span>{colour}</span>
    </div>
  );
};

const ticketEventColumns = [
  {
    name: "Name",
    render: (row: TicketEvent) => row.name,
  },
  {
    name: "Status",
    render: (row: TicketEvent) => renderStatusBadge(row.status, row.end_date),
  },
  {
    name: "External reference",
    render: (row: TicketEvent) => row.external_reference,
  },
  {
    name: "Code",
    render: (row: TicketEvent) => row.code,
  },
  {
    name: "Start date",
    render: (row: TicketEvent) => row.start_date || "-",
  },
  {
    name: "End date",
    render: (row: TicketEvent) => row.end_date || "-",
  },
  {
    name: "Location",
    render: (row: TicketEvent) => parseLocation(row.location),
  },
];

export default ticketEventColumns;
